import {
  WiseOwlBlock,
  WiseOwlBlockType,
  MessageBlock,
  MessageBlockRole,
  WiseOwlBlockDisplayType,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";
import { v4 as uuidv4 } from "uuid";

export const hasMessages = (blocks: WiseOwlBlock[]): boolean => {
  return !!blocks.find((block) => block.type === WiseOwlBlockType.Message);
};

/**
 * Used within WiseOwlChat to help render UI component(s)
 * if last message is from author
 */
export const lastMessageFromAuthor = (blocks: WiseOwlBlock[]): boolean => {
  if (blocks.length) {
    const messageBlocks = blocks.filter(
      (block) => block.type === WiseOwlBlockType.Message
    ) as MessageBlock[];
    return (
      messageBlocks[messageBlocks.length - 1]?.values.role ===
      MessageBlockRole.User
    );
  }
  return false;
};

/**
 * Used within wiseOwlView on OSC to replace a block at a given
 * indexId with a new block.
 */
export const spliceBlocks = (
  blocks: WiseOwlBlock[],
  index: number,
  updatedBlock?: WiseOwlBlock
): WiseOwlBlock[] => {
  const newBlocks = [...blocks];

  updatedBlock
    ? newBlocks.splice(index, 1, updatedBlock)
    : newBlocks.splice(index, 1);

  return newBlocks;
};

export const findLastFailedUserMessage = (
  blocks: WiseOwlBlock[],
  retryId: string
) => {
  const failedMessageIndex = blocks
    .slice()
    .findIndex(
      (block) =>
        block.type === WiseOwlBlockType.Message &&
        block?.values.retryId === retryId
    );

  const failedMessage = blocks[failedMessageIndex] as MessageBlock;

  return { failedMessageIndex, failedMessage };
};

/**
 * Find the user prompt message that resulted in an assistant message error.
 * @param blocks
 * @returns
 */
export const findUserPromptMessageToRegenerateAssistantMessage = (
  blocks,
  regenerationId
): [failedUserPromptMessage: MessageBlock, failedUserPromptIndex: number] => {
  const regenerateMessageIndex = blocks
    .slice()
    .findIndex(
      (block) =>
        block.type === WiseOwlBlockType.Message &&
        block?.values.id === regenerationId
    );
  const userPromptIndex =
    regenerateMessageIndex > 0 ? regenerateMessageIndex - 1 : blocks.length - 1;
  return [blocks[userPromptIndex] as MessageBlock, userPromptIndex];
};

/**
 * Used within wiseOwlView on OSC to replace a block at a given
 * indexId with a new block.
 */
export const setUserMessageBlockFailed = (
  messageBlock: MessageBlock
): MessageBlock => {
  return {
    ...messageBlock,
    values: { ...messageBlock.values, success: false },
  };
};

/**
 * Creates a new user message given input and prop for retry logic
 */
export const createNewUserMessageBlock = (
  input: string,
  createChatOnRetry: boolean
): MessageBlock => {
  return {
    type: WiseOwlBlockType.Message,
    display: WiseOwlBlockDisplayType.Persistent,
    values: {
      id: "user-message",
      message: input,
      role: MessageBlockRole.User,
      isComplete: true,
      delta: null,
      retryId: uuidv4(),
      createChatOnRetry,
    },
  };
};
